import { Progress, ButtonV2, TooltipV2, AvatarGroupV2, AvatarV2 } from '@cimplico/components';
import { CheckCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Workpaper, WorkpaperCompleteStatusEnum, WorkpaperRoleEnum, WorkpaperStatusEnum } from '../../types/workpaper';
import { calcRelativeTime, formatDate, formatMixedCase } from '../../utilities/formatting';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import PageTitle from '../../components/PageTitle';
import CertificationModal from './certification/CertificationModal';
import CompleteWorkpaperDialog from './complete/CompleteWorkpaperDialog';
import ExportWorkpaperDialog from './complete/ExportWorkpaperDialog';
import ReopenWorkpaperDialog from './reopen/ReopenWorkpaperDialog';
import plural from '../../utilities/plural';
import NavBreadcrumbs from '../../components/nav-breadcrumbs/NavBreadcrumbs';
import { useWorkpaperCertification } from '../../queries/use-workpaper-certification';
import { noRemainingActiveEntities } from '../new-workpaper/utils';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { useFirmContext } from '../../providers/FirmProvider';
import { useWorkpaper } from '../../queries/use-workpaper';

type DashboardOverviewProps = {
    workpaper: Workpaper;
};

function DashboardOverview({ workpaper }: DashboardOverviewProps): JSX.Element {
    const [action, setAction] = useState<string | undefined>();
    const firm = useFirmContext();
    const subscription = firm.subscription;
    const { isAdmin, isExternalUser } = useIsFirmRole();
    const { role, completeOutcome, priorWorkpaperId, entity } = useWorkpaperContext();

    const { shortId } = useFirmContext();
    const priorWorkpaperQuery = useWorkpaper(priorWorkpaperId);
    const priorWorkpaper = priorWorkpaperQuery.data?.workpaper;

    const certificationOverviewQuery = useWorkpaperCertification(workpaper.id, { overview: true });

    const certification = certificationOverviewQuery.data?.certification;

    const preparer = certification?.preparer;
    const reviewer = certification?.reviewer;
    const approver = certification?.approver;

    const preparerCertified = !preparer || (preparer && !!preparer.certifiedOn && !!preparer.certifiedBy);
    const reviewerCertified = !reviewer || (reviewer && !!reviewer.certifiedOn && !!reviewer.certifiedBy);
    const approverCertified = !approver || (approver && !!approver.certifiedOn && !!approver.certifiedBy);
    const allCertified = preparerCertified && reviewerCertified && approverCertified;

    const roleOrder = [WorkpaperRoleEnum.APPROVER, WorkpaperRoleEnum.REVIEWER, WorkpaperRoleEnum.PREPARER, WorkpaperRoleEnum.GUEST];

    const disableReopenHint = noRemainingActiveEntities(firm, entity, subscription)
        ? 'Cannot reopen a workpaper for a non-active entity as your firm has reached its allowance. Please contact your Firm Administrator to add more entities.'
        : undefined;

    return (
        <div>
            {action === 'CERTIFY' && (
                <CertificationModal
                    onClose={(completePrompt): void => setAction(completePrompt ? 'COMPLETE' : undefined)}
                    workpaper={workpaper}
                />
            )}
            <ReopenWorkpaperDialog isOpen={action === 'REOPEN'} onClose={(): void => setAction(undefined)} workpaperId={workpaper.id} />
            <CompleteWorkpaperDialog isOpen={action === 'COMPLETE'} onClose={(): void => setAction(undefined)} workpaperId={workpaper.id} />
            <ExportWorkpaperDialog isOpen={action === 'EXPORT'} onClose={(): void => setAction(undefined)} workpaperId={workpaper.id} />
            <NavBreadcrumbs hideWorkpaperName />
            {/* TITLE + CERTIFICATION */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                    <PageTitle title={`${formatDate(workpaper.periodEnd)} - ${workpaper.name}`} />
                    {workpaper.private && (
                        <div>
                            <TooltipV2 message="This workpaper is private.">
                                <LockClosedIcon className="h-5 w-5 text-gray-900" />
                            </TooltipV2>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-8 w-fit pl-2">
                    <p className="text-sm text-gray-600">
                        {workpaper.status === WorkpaperStatusEnum.COMPLETE &&
                        workpaper.completedInDays !== undefined &&
                        workpaper.completedInDays !== null
                            ? `Completed in ${workpaper.completedInDays} ${plural(workpaper.completedInDays, 'day', 'days')}`
                            : `Due ${calcRelativeTime(new Date(workpaper.dueDate))}`}
                    </p>
                    <div className="flex items-center gap-8 w-fit" data-intercom-target="workpaper-certification-button">
                        <div className="w-[200px]">
                            <Progress percent={workpaper.progressPercent} showProgressLabel title="Workpaper progress" />
                        </div>
                        {workpaper.status !== WorkpaperStatusEnum.COMPLETE && !allCertified && (
                            <ButtonV2 type="primary" label="Certification" size="sm" onClick={(): void => setAction('CERTIFY')} />
                        )}
                        {allCertified && workpaper.status !== WorkpaperStatusEnum.COMPLETE && (
                            <button className="flex text-sm gap-1 text-green-700" onClick={(): void => setAction('CERTIFY')}>
                                <CheckCircleIcon className="h-5 w-5" />
                                Certified
                            </button>
                        )}
                        {workpaper.status !== WorkpaperStatusEnum.COMPLETE && allCertified && role === WorkpaperRoleEnum.APPROVER && (
                            <ButtonV2
                                type="primary"
                                label={completeOutcome?.status === WorkpaperCompleteStatusEnum.COMPLETING ? 'Completing...' : 'Complete'}
                                size="sm"
                                onClick={(): void => setAction('COMPLETE')}
                                isDisabled={completeOutcome?.status === WorkpaperCompleteStatusEnum.COMPLETING}
                                isLoading={completeOutcome?.status === WorkpaperCompleteStatusEnum.COMPLETING}
                            />
                        )}
                        {workpaper.status === WorkpaperStatusEnum.COMPLETE && (
                            <>
                                <button className="flex text-sm gap-1 text-green-700" onClick={(): void => setAction('CERTIFY')}>
                                    <CheckCircleIcon className="h-5 w-5" />
                                    Completed
                                </button>
                                <ButtonV2
                                    type="secondary"
                                    label="Reopen"
                                    size="sm"
                                    onClick={(): void => setAction('REOPEN')}
                                    isDisabled={!!disableReopenHint || (!isAdmin && role !== WorkpaperRoleEnum.APPROVER) || isExternalUser}
                                    disabledHint={disableReopenHint}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            {/* PRIOR WORKPAPER */}
            {priorWorkpaperId && priorWorkpaper && (
                <>
                    <Link
                        to={`/${shortId}/workpapers/${workpaper.priorWorkpaperId}/dashboard`}
                        className="text-sm text-primary flex items-center gap-1"
                        target="_blank"
                    >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        {`Open Prior: ${priorWorkpaper.name}`}
                    </Link>
                </>
            )}
            {/* ASSIGNED */}
            <div className="mt-2">
                <AvatarGroupV2 size="sm">
                    {workpaper.assigned
                        .sort((a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role))
                        .sort((a) => (a.certified ? -1 : 1))
                        .map((assignee) => {
                            return (
                                <AvatarV2
                                    key={assignee.id || assignee.email}
                                    badge={
                                        assignee.certified && (
                                            <div className="bg-green-600 rounded-full p-0.5">
                                                <CheckIcon className="w-2 h-2 text-white" />
                                            </div>
                                        )
                                    }
                                    src={assignee.avatarUrl}
                                    alt={assignee.firstName && assignee.lastName ? assignee.firstName[0] + assignee.lastName[0] : undefined}
                                    color={assignee.profileHexColour}
                                    tooltipDirection="bottom"
                                    tooltip={`${
                                        assignee.firstName && assignee.lastName
                                            ? `${assignee.firstName} ${assignee.lastName}`
                                            : assignee.email
                                    } (${formatMixedCase(assignee.role)})`}
                                />
                            );
                        })}
                </AvatarGroupV2>
            </div>
        </div>
    );
}
export default DashboardOverview;
